import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';

const ADMIN_EMAIL = 'eduardo.na@live.com';

const STATUS_LABEL = {
  pendente: 'Pendente',
  aprovado: 'Aprovado',
  em_andamento: 'Em andamento',
  devolvido: 'Devolvido',
  cancelado: 'Cancelado',
  recusado: 'Recusado',
};

const STATUS_COR = {
  pendente: 'bg-amber-50 text-amber-DEFAULT',
  aprovado: 'bg-brand-50 text-brand-600',
  em_andamento: 'bg-success-50 text-success',
  devolvido: 'bg-paper-2 text-ink-500',
  cancelado: 'bg-coral-50 text-coral',
  recusado: 'bg-coral-50 text-coral',
};

function formatarData(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function sortBy(arr, col, dir) {
  if (!col) return arr;
  return [...arr].sort((a, b) => {
    const va = a[col] ?? '';
    const vb = b[col] ?? '';
    if (va < vb) return dir === 'asc' ? -1 : 1;
    if (va > vb) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}

function ThSort({ label, col, sortCol, sortDir, onSort, center }) {
  const ativo = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      className={`${center ? 'text-center' : 'text-left'} px-5 py-3 text-[11px] font-semibold uppercase tracking-widest cursor-pointer select-none transition-colors ${ativo ? 'text-brand-600' : 'text-ink-400 hover:text-ink-600'}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className="text-[10px] leading-none opacity-60">
          {ativo ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </span>
    </th>
  );
}

function StatCard({ label, valor, sub, destaque }) {
  return (
    <div className={`bg-white rounded-2xl p-5 border ${destaque ? 'border-coral/40' : 'border-line'}`}>
      <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-widest">{label}</p>
      <p className={`text-4xl font-bold mt-2 ${destaque ? 'text-coral' : 'text-ink-900'}`}>
        {valor ?? <span className="text-ink-300">—</span>}
      </p>
      {sub && <p className="text-xs text-ink-400 mt-1">{sub}</p>}
    </div>
  );
}

// ─── DASHBOARD ───────────────────────────────────────────────────────────────

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [atividade, setAtividade] = useState(null);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    Promise.all([
      supabase.rpc('admin_stats'),
      supabase.rpc('admin_atividade_recente'),
    ]).then(([s, a]) => {
      if (s.error) setErro(`admin_stats: ${s.error.message}`);
      else setStats(s.data);
      if (!a.error) setAtividade(a.data);
      setCarregando(false);
    });
  }, []);

  if (carregando) return <div className="p-8 text-ink-400 text-sm">Carregando...</div>;

  return (
    <div className="p-8 max-w-6xl">
      <h1 className="text-2xl font-bold text-ink-900 mb-1">Dashboard</h1>
      <p className="text-sm text-ink-400 mb-8">Visão geral do app</p>

      {erro && (
        <div className="mb-6 bg-coral-50 border border-coral/20 rounded-xl p-4 text-sm text-coral font-mono">
          Erro nas funções SQL: {erro}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <StatCard
          label="Usuários"
          valor={stats?.total_usuarios}
          sub={stats ? `+${stats.novos_hoje} hoje · +${stats.novos_semana} na semana` : null}
        />
        <StatCard
          label="Livros"
          valor={stats?.total_livros}
          sub={stats ? `+${stats.livros_novos_hoje} hoje` : null}
        />
        <StatCard
          label="Empréstimos ativos"
          valor={stats?.emprestimos_ativos}
          sub={stats ? `${stats.emprestimos_devolvidos_mes} devolvidos este mês` : null}
        />
        <StatCard label="Clubes" valor={stats?.total_clubes} />
      </div>

      <div className="grid grid-cols-3 gap-4 mb-10">
        <StatCard
          label="Em atraso"
          valor={stats?.emprestimos_atrasados}
          destaque={(stats?.emprestimos_atrasados ?? 0) > 0}
        />
        <StatCard
          label="Inadimplentes"
          valor={stats?.usuarios_inadimplentes}
          destaque={(stats?.usuarios_inadimplentes ?? 0) > 0}
        />
        <StatCard
          label="Solic. paradas +3 dias"
          valor={stats?.solicitacoes_paradas}
          destaque={(stats?.solicitacoes_paradas ?? 0) > 0}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl border border-line p-5">
          <h2 className="text-sm font-semibold text-ink-700 mb-4">Últimos usuários</h2>
          <div className="space-y-4">
            {(atividade?.usuarios || []).map(u => (
              <div key={u.id} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-ink-900">{u.nome}</p>
                  <p className="text-xs text-ink-400">{u.slug}</p>
                </div>
                <p className="text-xs text-ink-300">{formatarData(u.created_at)}</p>
              </div>
            ))}
            {!atividade?.usuarios?.length && <p className="text-xs text-ink-300">Nenhum dado</p>}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-line p-5">
          <h2 className="text-sm font-semibold text-ink-700 mb-4">Últimos livros</h2>
          <div className="space-y-4">
            {(atividade?.livros || []).map(l => (
              <div key={l.id} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-ink-900 truncate max-w-[160px]">{l.titulo}</p>
                  <p className="text-xs text-ink-400">{l.dono_nome}</p>
                </div>
                <p className="text-xs text-ink-300">{formatarData(l.created_at)}</p>
              </div>
            ))}
            {!atividade?.livros?.length && <p className="text-xs text-ink-300">Nenhum dado</p>}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-line p-5">
          <h2 className="text-sm font-semibold text-ink-700 mb-4">Últimos empréstimos</h2>
          <div className="space-y-4">
            {(atividade?.emprestimos || []).map(e => (
              <div key={e.id} className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-medium text-ink-900 truncate max-w-[150px]">{e.livro_titulo}</p>
                  <p className="text-xs text-ink-400">{e.leitor_nome} ← {e.dono_nome}</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${STATUS_COR[e.status] || 'bg-paper-2 text-ink-500'}`}>
                  {STATUS_LABEL[e.status] || e.status}
                </span>
              </div>
            ))}
            {!atividade?.emprestimos?.length && <p className="text-xs text-ink-300">Nenhum dado</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── USUÁRIOS ────────────────────────────────────────────────────────────────

function Usuarios() {
  const [lista, setLista] = useState([]);
  const [busca, setBusca] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  const carregar = useCallback(async (termo) => {
    setCarregando(true);
    const { data } = await supabase.rpc('admin_listar_usuarios', { p_busca: termo || '', p_limite: 100, p_offset: 0 });
    setLista(data || []);
    setCarregando(false);
  }, []);

  useEffect(() => { carregar(''); }, [carregar]);

  const toggleInadimplente = async (user) => {
    await supabase.rpc('admin_set_inadimplente', { p_user_id: user.id, p_valor: !user.inadimplente });
    setLista(prev => prev.map(u => u.id === user.id ? { ...u, inadimplente: !u.inadimplente } : u));
  };

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const listaOrdenada = sortBy(lista, sortCol, sortDir);

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Usuários</h1>
          <p className="text-sm text-ink-400">{lista.length} cadastrados</p>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Buscar..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && carregar(busca)}
            className="border border-line rounded-xl px-3 py-2 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
          />
          <button
            onClick={() => carregar(busca)}
            className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-xl hover:bg-brand-700 transition-colors"
          >
            Buscar
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-line overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-paper border-b border-line">
            <tr>
              <ThSort label="Nome" col="nome" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <ThSort label="Username" col="username" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <ThSort label="Livros" col="total_livros" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} center />
              <ThSort label="Emp. ativos" col="emprestimos_ativos" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} center />
              <ThSort label="Cadastro" col="created_at" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <ThSort label="Inadimplente" col="inadimplente" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} center />
            </tr>
          </thead>
          <tbody className="divide-y divide-line-soft">
            {carregando ? (
              <tr><td colSpan={6} className="text-center py-10 text-ink-300 text-sm">Carregando...</td></tr>
            ) : listaOrdenada.map(u => (
              <tr key={u.id} className="hover:bg-paper transition-colors">
                <td className="px-5 py-3.5 font-medium text-ink-900">{u.nome}</td>
                <td className="px-5 py-3.5 text-ink-400 text-xs font-mono">{u.username || '—'}</td>
                <td className="px-5 py-3.5 text-center text-ink-700">{u.total_livros}</td>
                <td className="px-5 py-3.5 text-center text-ink-700">{u.emprestimos_ativos}</td>
                <td className="px-5 py-3.5 text-ink-300 text-xs">{formatarData(u.created_at)}</td>
                <td className="px-5 py-3.5 text-center">
                  <button
                    onClick={() => toggleInadimplente(u)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                      u.inadimplente
                        ? 'bg-coral-50 text-coral hover:bg-coral/20'
                        : 'bg-paper text-ink-400 hover:bg-paper-2'
                    }`}
                  >
                    {u.inadimplente ? 'Sim' : 'Não'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── LIVROS ──────────────────────────────────────────────────────────────────

function Livros() {
  const [lista, setLista] = useState([]);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    supabase.rpc('admin_listar_livros', { p_limite: 100, p_offset: 0 })
      .then(({ data, error }) => {
        if (error) setErro(error.message);
        else setLista(Array.isArray(data) ? data : (data || []));
        setCarregando(false);
      });
  }, []);

  const removerLivro = async (id) => {
    if (!window.confirm('Remover este livro? Essa ação não pode ser desfeita.')) return;
    await supabase.from('livros').delete().eq('id', id);
    setLista(prev => prev.filter(l => l.id !== id));
  };

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const listaOrdenada = sortBy(lista, sortCol, sortDir);

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-ink-900">Livros</h1>
        <p className="text-sm text-ink-400">{lista.length} cadastrados</p>
      </div>

      {erro && (
        <div className="mb-4 bg-coral-50 border border-coral/20 rounded-xl p-4 text-sm text-coral font-mono">{erro}</div>
      )}

      <div className="bg-white rounded-2xl border border-line overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-paper border-b border-line">
            <tr>
              <ThSort label="Título" col="titulo" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <ThSort label="Autor" col="autor" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <ThSort label="Dono" col="dono_nome" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <ThSort label="Disponível" col="disponivel" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} center />
              <ThSort label="Cadastro" col="created_at" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line-soft">
            {carregando ? (
              <tr><td colSpan={6} className="text-center py-10 text-ink-300 text-sm">Carregando...</td></tr>
            ) : listaOrdenada.map(l => (
              <tr key={l.id} className="hover:bg-paper transition-colors">
                <td className="px-5 py-3.5 font-medium text-ink-900 max-w-[200px] truncate">{l.titulo}</td>
                <td className="px-5 py-3.5 text-ink-500 max-w-[160px] truncate">{l.autor}</td>
                <td className="px-5 py-3.5 text-ink-500">{l.dono_nome}</td>
                <td className="px-5 py-3.5 text-center">
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${l.disponivel ? 'bg-success-50 text-success' : 'bg-paper text-ink-400'}`}>
                    {l.disponivel ? 'Sim' : 'Não'}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-ink-300 text-xs">{formatarData(l.created_at)}</td>
                <td className="px-5 py-3.5 text-right">
                  <button onClick={() => removerLivro(l.id)} className="text-xs text-coral/70 hover:text-coral font-medium">Remover</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── EMPRÉSTIMOS ─────────────────────────────────────────────────────────────

const FILTROS_EMP = [
  { id: '', label: 'Todos' },
  { id: 'pendente', label: 'Pendentes' },
  { id: 'em_andamento', label: 'Em andamento' },
  { id: 'atrasado', label: 'Atrasados' },
  { id: 'devolvido', label: 'Devolvidos' },
  { id: 'cancelado', label: 'Cancelados' },
];

function Emprestimos() {
  const [lista, setLista] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    setCarregando(true);
    const statusParam = filtro === 'atrasado' ? '' : filtro;
    supabase.rpc('admin_listar_emprestimos', { p_limite: 100, p_offset: 0, p_status: statusParam })
      .then(({ data }) => {
        let resultado = Array.isArray(data) ? data : (data || []);
        if (filtro === 'atrasado') {
          const hoje = new Date().toISOString().split('T')[0];
          resultado = resultado.filter(e =>
            ['em_andamento', 'aprovado'].includes(e.status) && e.data_devolucao && e.data_devolucao < hoje
          );
        }
        setLista(resultado);
        setCarregando(false);
      });
  }, [filtro]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const listaOrdenada = sortBy(lista, sortCol, sortDir);

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-ink-900">Empréstimos</h1>
        <p className="text-sm text-ink-400">{lista.length} registros</p>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {FILTROS_EMP.map(f => (
          <button
            key={f.id}
            onClick={() => setFiltro(f.id)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              filtro === f.id
                ? 'bg-brand-600 text-white'
                : 'bg-white border border-line text-ink-500 hover:bg-paper'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-line overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-paper border-b border-line">
            <tr>
              <ThSort label="Livro" col="livro_titulo" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <ThSort label="Dono" col="dono_nome" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <ThSort label="Leitor" col="leitor_nome" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <ThSort label="Status" col="status" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} center />
              <ThSort label="Devolução" col="data_devolucao" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <ThSort label="Criado" col="created_at" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
            </tr>
          </thead>
          <tbody className="divide-y divide-line-soft">
            {carregando ? (
              <tr><td colSpan={6} className="text-center py-10 text-ink-300 text-sm">Carregando...</td></tr>
            ) : listaOrdenada.map(e => (
              <tr key={e.id} className="hover:bg-paper transition-colors">
                <td className="px-5 py-3.5 font-medium text-ink-900 max-w-[180px] truncate">{e.livro_titulo}</td>
                <td className="px-5 py-3.5 text-ink-500">{e.dono_nome}</td>
                <td className="px-5 py-3.5 text-ink-500">{e.leitor_nome}</td>
                <td className="px-5 py-3.5 text-center">
                  <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-semibold ${STATUS_COR[e.status] || 'bg-paper text-ink-400'}`}>
                    {STATUS_LABEL[e.status] || e.status}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-ink-300 text-xs">{formatarData(e.data_devolucao)}</td>
                <td className="px-5 py-3.5 text-ink-300 text-xs">{formatarData(e.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── CLUBES ──────────────────────────────────────────────────────────────────

function Clubes() {
  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    supabase.rpc('admin_listar_clubes', { p_limite: 100, p_offset: 0 })
      .then(({ data }) => { setLista(Array.isArray(data) ? data : (data || [])); setCarregando(false); });
  }, []);

  const removerClube = async (id) => {
    if (!window.confirm('Remover este clube? Essa ação não pode ser desfeita.')) return;
    await supabase.from('clubes').delete().eq('id', id);
    setLista(prev => prev.filter(c => c.id !== id));
  };

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const listaOrdenada = sortBy(lista, sortCol, sortDir);

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-ink-900">Clubes</h1>
        <p className="text-sm text-ink-400">{lista.length} cadastrados</p>
      </div>

      <div className="bg-white rounded-2xl border border-line overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-paper border-b border-line">
            <tr>
              <ThSort label="Nome" col="nome" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <ThSort label="Slug" col="slug" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <ThSort label="Membros" col="total_membros" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} center />
              <ThSort label="Livros" col="total_livros" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} center />
              <ThSort label="Criado" col="created_at" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line-soft">
            {carregando ? (
              <tr><td colSpan={6} className="text-center py-10 text-ink-300 text-sm">Carregando...</td></tr>
            ) : listaOrdenada.map(c => (
              <tr key={c.id} className="hover:bg-paper transition-colors">
                <td className="px-5 py-3.5 font-medium text-ink-900">{c.nome}</td>
                <td className="px-5 py-3.5 text-ink-300 text-xs font-mono">{c.slug}</td>
                <td className="px-5 py-3.5 text-center text-ink-700">{c.total_membros}</td>
                <td className="px-5 py-3.5 text-center text-ink-700">{c.total_livros}</td>
                <td className="px-5 py-3.5 text-ink-300 text-xs">{formatarData(c.created_at)}</td>
                <td className="px-5 py-3.5 text-right">
                  <button onClick={() => removerClube(c.id)} className="text-xs text-coral/70 hover:text-coral font-medium">Remover</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── REPORTES ─────────────────────────────────────────────────────────────────

const CAT_LABEL = { bug: 'Problema', sugestao: 'Sugestão', outro: 'Outro' };
const CAT_COR = {
  bug: 'bg-red-50 text-red-500',
  sugestao: 'bg-brand-50 text-brand-600',
  outro: 'bg-paper-2 text-ink-500',
};

function Reportes() {
  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    supabase.from('reportes').select('*').order('created_at', { ascending: false }).limit(200)
      .then(({ data }) => { setLista(data || []); setCarregando(false); });
  }, []);

  const marcarLido = async (id) => {
    await supabase.from('reportes').update({ lido: true }).eq('id', id);
    setLista(prev => prev.map(r => r.id === id ? { ...r, lido: true } : r));
  };

  const naoLidos = lista.filter(r => !r.lido).length;

  return (
    <div className="p-8 max-w-4xl">
      <div className="mb-6 flex items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Problemas e sugestões</h1>
          <p className="text-sm text-ink-400">{lista.length} enviados · {naoLidos} não lidos</p>
        </div>
        {naoLidos > 0 && (
          <span className="ml-2 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-500">{naoLidos}</span>
        )}
      </div>

      {carregando ? (
        <p className="text-sm text-ink-300 text-center py-20">Carregando...</p>
      ) : lista.length === 0 ? (
        <p className="text-sm text-ink-300 text-center py-20">Nenhum reporte ainda.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {lista.map(r => (
            <div key={r.id}
              className={`bg-white rounded-2xl border p-5 transition-opacity ${r.lido ? 'opacity-50' : 'border-line'}`}
              style={!r.lido ? {borderColor:'#CFC5F7', background:'#FAF8FF'} : {}}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${CAT_COR[r.categoria] || 'bg-paper-2 text-ink-500'}`}>
                    {CAT_LABEL[r.categoria] || r.categoria}
                  </span>
                  <span className="text-xs text-ink-400">{r.user_nome || 'Anônimo'}</span>
                  {r.user_email && <span className="text-xs text-ink-300 font-mono">{r.user_email}</span>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-ink-300">{formatarData(r.created_at)}</span>
                  {!r.lido && (
                    <button onClick={() => marcarLido(r.id)}
                      className="text-xs text-brand-600 hover:underline font-medium">
                      Marcar lido
                    </button>
                  )}
                </div>
              </div>
              <p className="text-sm text-ink-700 leading-relaxed whitespace-pre-wrap">{r.texto}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────

const SECOES = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'usuarios', label: 'Usuários' },
  { id: 'livros', label: 'Livros' },
  { id: 'emprestimos', label: 'Empréstimos' },
  { id: 'clubes', label: 'Clubes' },
  { id: 'reportes', label: 'Reportes' },
];

export default function Admin() {
  const navigate = useNavigate();
  const [autenticado, setAutenticado] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [secaoAtiva, setSecaoAtiva] = useState('dashboard');
  const [emailSessao, setEmailSessao] = useState('');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const email = session?.user?.email || '';
      setEmailSessao(email);
      if (email === ADMIN_EMAIL) setAutenticado(true);
      setCarregando(false);
    });
  }, []);

  if (carregando) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <p className="text-sm text-ink-400">Verificando acesso...</p>
      </div>
    );
  }

  if (!autenticado) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="text-center">
          <p className="text-ink-700 font-semibold">Acesso negado</p>
          <p className="text-sm text-ink-400 mt-1">
            Email na sessão: <span className="font-mono">{emailSessao || '(sem sessão)'}</span>
          </p>
          <button onClick={() => navigate('/home')} className="mt-4 text-sm text-brand-600 underline">
            Voltar ao app
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper flex">
      {/* Sidebar */}
      <div className="w-52 bg-brand-600 flex flex-col shrink-0 sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-white/10 flex items-center gap-3">
          <img src="/logo192.png" alt="Logo" className="w-8 h-8 rounded-lg" />
          <div>
            <p className="text-[11px] font-bold text-white/60 uppercase tracking-widest leading-none">Biblioteca de Bolso</p>
            <p className="text-xs font-semibold text-white mt-0.5">Painel Admin</p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {SECOES.map(s => (
            <button
              key={s.id}
              onClick={() => setSecaoAtiva(s.id)}
              className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                secaoAtiva === s.id
                  ? 'bg-white/15 text-white'
                  : 'text-white/60 hover:bg-white/10 hover:text-white'
              }`}
            >
              {s.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button
            onClick={() => navigate('/home')}
            className="w-full text-left px-3 py-2.5 rounded-xl text-sm text-white/40 hover:bg-white/10 hover:text-white/70 transition-colors"
          >
            Voltar ao app
          </button>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="flex-1 overflow-auto">
        {secaoAtiva === 'dashboard' && <Dashboard />}
        {secaoAtiva === 'usuarios' && <Usuarios />}
        {secaoAtiva === 'livros' && <Livros />}
        {secaoAtiva === 'emprestimos' && <Emprestimos />}
        {secaoAtiva === 'clubes' && <Clubes />}
        {secaoAtiva === 'reportes' && <Reportes />}
      </div>
    </div>
  );
}
